import {defineStore} from 'pinia';
import { AxiosRequestConfig } from "axios";
import handleTeacherError from "@/util/handle_teacher_error";
import apiRequest from '@/util/api-request';

interface Conditions {
  academicYears: { value: string, name: string }[];
  classrooms: { value: string, name: string }[];
  learningStatusStudentBaseUrl: string;
}

interface LearningStatusClass {
  learning_status_year_base_url: string;
  conditions: Conditions;
  progress_data: ProgressData;
}

interface ProgressDetailData {
  id: number;
  name: string;
  number: string;
  progressRate: number;
  correctAnswerRateInitial: number;
  correctAnswerRateLatest: number;
  correctAnswerRateDifference: number;
  numberOfStars: number;
  studyCount: number;
  studiedQuestions: number;
  studiedHoursPrevious: number;
  studiedHoursLatest: number;
  studiedHoursDifference: number;
  studiedDays: number;
  latestStudyDate: string;
}

interface ProgressData {
  averageData: ProgressDetailData;
  datasets: ProgressDetailData[];
}

// FIXME: 一旦 T8-1 (learning_status_years.ts) のものを持ってきただけなので、同様に修正が必要かも。
const subjectIdMap: { [value: string] : number } = {
  // FIXME: ゆくゆくは教科IDもサーバー側から取得した方が良いかも
  //  もしくは、この変換表はサーバー側に持ったほうがよいのかな...
  //  => FIXME: ひとまず consts に引っ越す, 引き当ては呼び出し元 component のほうでやる
  "all": 0,
  "kokugo": 1,
  "shakai": 2,
  "sugaku": 3,
  "rika": 4,
  "eigo": 5,
}

const useLearningStatusClassStore = defineStore('learningStatusClassStore', {
  // state
  state: (): LearningStatusClass => {
    return {
      learning_status_year_base_url: "",
      conditions: null,
      progress_data: null,
    }
  },
  // getters
  getters: {
    learningStatusYearBaseUrl: (state) => {
      return state.learning_status_year_base_url
    },
    classConditions: (state) => {
      return state.conditions
    },
    progressData: (state) => {
      return state.progress_data
    }
  },
  // actions
  actions: {
    async fetchLearningStatusClassConditions({academic_year}) {
      try {
        const config: AxiosRequestConfig = {
          method: "get",
          url: "/teacher/api/learning_status/class/conditions/",
          headers: {
            "Content-Type": "application/json",
          },
          params: {
            academic_year: academic_year,
          },
        };
        const conditions = await apiRequest(config);
  
        // 学習状況（学年）のBaseURLを取得
        this.learning_status_year_base_url = conditions.data.learningStatusYearBaseUrl
  
        // 学習状況（学年）のBaseURL以外の検索条件の格納
        if (conditions.data && Object.keys(conditions.data).length > 1) {
          this.conditions = {
            "academicYears": conditions.data.academicYears,
            "classrooms": conditions.data.classrooms,
            "learningStatusStudentBaseUrl": conditions.data.learningStatusStudentBaseUrl
          }
        }
      } catch (e) {
        // バックエンドで明示的に raise したエラーの場合は、
        // 「学習状況（学年）のBaseURL」だけは返る
        this.learning_status_year_base_url = e.response.data.error?.data.learningStatusYearBaseUrl
        handleTeacherError(e)
      }
    },
    async fetchLearningStatusClassProgressData({classroom_uuid, subject, period, work}) {
      try {
        const config: AxiosRequestConfig = {
          method: "get",
          url: "/teacher/api/learning_status/class/study_progress/",
          headers: {
            "Content-Type": "application/json",
          },
          params: {
            classroom_uuid: classroom_uuid,
            subject: subjectIdMap[subject],
            period: period,
            ...(work && { work_id: work }),
          },
        };
        const progressData = await apiRequest(config);
        if (progressData.data && Object.keys(progressData.data).length > 0) {
          this.progress_data = progressData.data
        }
      } catch (e) {
        this.progress_data = null
        handleTeacherError(e)
      }
    },
  }
})

export {useLearningStatusClassStore, Conditions, ProgressData}